/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from "react";
import { Browser } from "@capacitor/browser";
import { AppLauncher } from "@capacitor/app-launcher";

import {
  IonPage,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonIcon,
  IonToggle,
  useIonRouter,
  IonItemGroup,
  IonRippleEffect,
  IonAlert,
  IonSkeletonText,
  isPlatform,
  useIonViewWillEnter,
} from "@ionic/react";
import {
  personOutline,
  phonePortraitOutline,
  bulbOutline,
  notificationsOutline,
  shareOutline,
  heartOutline,
  chevronForward,
  chatbubbleEllipsesOutline,
  happyOutline,
  documentTextOutline,
} from "ionicons/icons";
import { Preferences } from "@capacitor/preferences";

import Header from "../../components/Header";
import NewMessageIndicator from "../../components/engagement/NewMessageIndicator";
import AllPreferencesButton from "./debug/Preferences";
import LogViewer from "./debug/LogViewer";
import "./Settings.css";
import { useAuth } from "../../hooks/useAuth";
import { useBiometricAuth } from "../../hooks/useBiometrics";
import { useEngagementFeatures } from "../../hooks/useEngagementFeatures";
// E12d/R1 — the unread badge on CARE TEAM > Messages. In-app only, by ruling: this is
// how a patient discovers a clinician replied, so the number must come from the kernel
// and not from anything this app remembers.
import { useEngagementSync } from "../../hooks/useEngagementSync";
import { apiClient, CURRENT_ENV } from "../../services/api";
import { App } from "@capacitor/app";
import {
  requestHealthKitPermissions,
  openHealthSettings,
} from "../../services/healthkit";
import {
  ANDROID_CATALOG_NUMBER,
  APP_VERSION,
  HEALTH_APP_URL_ANDROID,
  HEALTH_APP_URL_IOS,
  IFU_URL,
  IOS_CATALOG_NUMBER,
  WEB_VERSION,
} from "../../utils/constants";
import { Health as CapGoHealth } from "@capgo/capacitor-health";
import crashlytics from "../../services/crashlytics";
import { Capacitor } from "@capacitor/core";
import {
  goToProviderPortal,
  portalSwitchUrl,
  SWITCH_TO_PORTAL_LABEL,
} from "../../services/staffPortal";

const ROUTES = {
  MY_ACCOUNT: "/settings/account",
  MY_DEVICES: "/settings/devices",
  TREATMENT_GOALS: "/settings/edit-goals",
  NOTIFICATIONS: "/settings/notifications",
  DATA_SHARING: "/settings/followers",
  PENDING_REQUESTS: "/settings/pending-requests",
  HEALTH_APP: "/settings/health-connect",
  BIO_METRIC: "/settings/biometric-data",
  HELP: "/settings/help",
  CONTACT_US: "/settings/contact-us",
  PRIVACY_POLICY: "/settings/privacy-policy",
  TERMS_OF_USE: "/settings/terms-of-use",
  BIOMETRIC_SETTINGS: "/settings/biometric-settings",
  HEALTH_PLUGIN_TESTER: "/settings/debug/health-plugin-tester",
  // Epic 12 patient side (E12c). ⛔ These screens call the KERNEL, not blaze.
  CARE_TEAM_MESSAGES: "/messages",
  DAILY_CHECK_IN: "/check-in",
  CARE_TEAM_SURVEYS: "/surveys",
};

// F-495 — screen-reader noun for the shared "New" on the Surveys row.
const SURVEYS_WAITING = { one: "survey waiting", many: "surveys waiting" };

type SettingsItemType = {
  id: string;
  label: string;
  icon: string;
  route?: string;
  badge?: string | number;
  action?: () => Promise<void>;
};

type SettingsSectionType = {
  id: string;
  label: string;
  mobileOnly?: boolean;
  items: SettingsItemType[];
};

const openHealthApp = async () => {
  try {
    const accessHealthValue = (
      await Preferences.get({ key: "accessHealthKit" })
    ).value;
    if (!accessHealthValue || accessHealthValue !== "true") {
      const result = await requestHealthKitPermissions();
      // If permissions were granted, set the flag so health data syncs automatically
      if (result.success) {
        await Preferences.set({ key: "accessHealthKit", value: "true" });
      }
      return;
    }
    await openHealthSettings();
  } catch (error) {
    console.error("Error opening Health App:", error);
  }
};
const SETTINGS_SECTIONS = [
  {
    id: "account",
    label: "ACCOUNT",
    items: [
      {
        id: "my-account",
        label: "My Account",
        icon: personOutline,
        route: ROUTES.MY_ACCOUNT,
      },
      {
        id: "my-devices",
        label: "My Devices",
        icon: phonePortraitOutline,
        route: ROUTES.MY_DEVICES,
      },
      {
        id: "treatment-goals",
        label: "My Goals",
        icon: bulbOutline,
        route: ROUTES.TREATMENT_GOALS,
      },
      {
        id: "notifications",
        label: "Notifications",
        icon: notificationsOutline,
        route: ROUTES.NOTIFICATIONS,
      },
    ],
  },
  {
    // ⭐ ITS OWN SECTION, above SOCIAL. "Data Sharing" under SOCIAL is about followers —
    // family and friends. This is the clinical relationship, and filing a message to a
    // clinician next to a follow request would be the wrong mental model for a patient
    // deciding where to ask a medical question.
    //
    // ⚠️ The whole section is hidden when no organization holds this patient's record — see
    // `hasOrganization` in the component. There is nothing behind these entries for an
    // unassociated patient, and a menu row that leads to an empty state is a dead end.
    id: "care-team",
    label: "CARE TEAM",
    items: [
      {
        id: "care-team-messages",
        label: "Messages",
        icon: chatbubbleEllipsesOutline,
        route: ROUTES.CARE_TEAM_MESSAGES,
      },
      {
        id: "daily-check-in",
        label: "Daily check-in",
        icon: happyOutline,
        route: ROUTES.DAILY_CHECK_IN,
      },
      // F-486 (UAT) — the 30/60/90-day surveys' own category. Shown while the organization
      // sends surveys, and while any survey is still waiting (history stays reachable).
      {
        id: "care-team-surveys",
        label: "Surveys",
        icon: documentTextOutline,
        route: ROUTES.CARE_TEAM_SURVEYS,
      },
    ],
  },
  {
    id: "social",
    label: "SOCIAL",
    items: [
      {
        id: "data-sharing",
        label: "Data Sharing",
        icon: shareOutline,
        route: ROUTES.DATA_SHARING,
      },
      // {
      //   id: "pending-requests",
      //   label: "Pending Requests",
      //   icon: shareOutline,
      //   route: ROUTES.PENDING_REQUESTS,
      // },
    ],
  },
  {
    id: "add-ons",
    label: "ADD-ONS",
    mobileOnly: true,
    items: [
      {
        id: "health-app",
        label: isPlatform("ios")
          ? "Apple Health Connect"
          : isPlatform("android")
          ? "Google Health Connect"
          : "Health App",
        icon: heartOutline,
        route: ROUTES.HEALTH_APP,
        action: openHealthApp,
      },
    ],
  },
  {
    id: "security",
    label: "SECURITY",
    mobileOnly: true,
    items: [
      {
        id: "biometric-settings",
        label: "Biometric Settings",
        icon: notificationsOutline,
        route: ROUTES.BIOMETRIC_SETTINGS,
      },
    ],
  },
  {
    id: "support",
    label: "SUPPORT",
    items: [
      // {
      //   id: "help",
      //   label: "Help",
      //   icon: personOutline,
      //   route: ROUTES.HELP,
      // },
      // {
      //   id: "contact-us",
      //   label: "Contact Us",
      //   icon: phonePortraitOutline,
      //   route: ROUTES.CONTACT_US,
      // },
      {
        id: "privacy-policy",
        label: "Privacy Policy",
        icon: bulbOutline,
        route: ROUTES.PRIVACY_POLICY,
      },
      {
        id: "terms-of-use",
        label: "Terms of Use",
        icon: notificationsOutline,
        route: ROUTES.TERMS_OF_USE,
      },

      {
        id: "ifu",
        label: "Instructions for Use",
        icon: bulbOutline,
        action: async () => {
          try {
            await Browser.open({ url: IFU_URL });
          } catch (error) {
            console.error("Error opening IFU URL:", error);
          }
        },
      },
    ],
  },
];

/**
 * R-753 (owner ruling 2): provider staff can always switch to the Arc Connect Provider Portal.
 * Mirrors the portal's "Switch to Patient App"; shown to staff only. Web: full-page navigation;
 * native: the system browser. The `?from=app` marker tells the portal where they came from.
 */
const PROVIDER_PORTAL_SECTION = {
  id: "provider-portal",
  label: "PROVIDER PORTAL",
  items: [
    {
      id: "switch-to-provider-portal",
      label: SWITCH_TO_PORTAL_LABEL,
      icon: shareOutline,
      action: () => goToProviderPortal(portalSwitchUrl()),
    },
  ],
};

/** F-885 — shown in CARE TEAM when the features call could not be answered. */
export const CARE_TEAM_LOAD_ERROR = "Your care team options couldn't be loaded.";
export const CARE_TEAM_RETRY = "Try again";

/**
 * F-513 — the placeholder shown until the engagement answers are in: one block per section,
 * with the same label strip and row height as the real thing, so the swap to the real list is
 * a single paint and nothing below it moves.
 */
const SettingsSkeleton: React.FC<{ sections: SettingsSectionType[] }> = ({ sections }) => (
  <div data-testid="settings-skeleton" aria-busy="true">
    {sections.map((section) => (
      <div className="section" key={section.id}>
        <div className="section-label">{section.label}</div>
        <IonList className="settings-list">
          <IonItemGroup>
            {section.items.map((item) => (
              <IonItem key={item.id} detail={false} className="settings-item">
                <IonLabel>
                  <IonSkeletonText animated style={{ width: "45%" }} />
                </IonLabel>
              </IonItem>
            ))}
          </IonItemGroup>
        </IonList>
      </div>
    ))}
  </div>
);

const SettingsPage: React.FC = () => {
  const router = useIonRouter();
  const { isDesktop, isStaff } = useAuth();
  // URS_12.6 — the menu mirrors the org's configuration rather than guessing at it.
  const {
    hasOrganization,
    features,
    isLoading: featuresLoading,
    error: featuresError,
    refresh: refreshFeatures,
  } = useEngagementFeatures();
  // ⭐ F-513 (UAT, Leah) — FETCHED IN PARALLEL WITH THE FEATURES, NOT AFTER THEM. This used to
  // be `useEngagementSync(hasOrganization)`, which could only start once the features call had
  // answered, and the CARE TEAM section was then painted a beat after every other section, so
  // the whole screen visibly shifted down. The kernel answers sync for a patient nobody holds
  // with an empty envelope and writes nothing (`POST /me/engagement/sync`, `assoc is None`), so
  // there is no reason to wait. Its counts are only ever READ inside the CARE TEAM section,
  // which is not rendered without an organization.
  const { sync, isLoading: syncLoading, refresh: refreshSync } = useEngagementSync(true);
  // F-513: nothing below the header is painted until BOTH answers are in, so every section
  // (CARE TEAM included, with the rows it will finally have) appears in one go. The skeleton
  // shown meanwhile has the shape of the screen, so the swap does not jump either.
  const sectionsLoading = featuresLoading || syncLoading;
  // F-514 — a tab page is cached, not remounted: coming back from Data Sharing after leaving or
  // switching an organization (or after the org changed its settings) must re-read which
  // organization holds the patient and what it allows, or the rows would describe the old one.
  const enteredOnce = useRef(false);
  useIonViewWillEnter(() => {
    if (!enteredOnce.current) {
      enteredOnce.current = true; // the hooks' own mount fetch already covers the first entry
      return;
    }
    void refreshFeatures();
    void refreshSync();
  });
  const debugMode = false;
  const [lastFetchedWeeklyStatus, setLastFetchedWeeklyStatus] = useState<
    string | null
  >(null);

  useEffect(() => {
    const fetchLastFetchedWeeklyStatus = async () => {
      const res = await Preferences.get({ key: "lastFetchedWeeklyStatus" });
      setLastFetchedWeeklyStatus(res.value);
    };
    fetchLastFetchedWeeklyStatus();
  }, []);

  const handleNavigation = (path: string, itemId: string) => {
    if (itemId === "my-account") {
      router.push(path, "forward", "push");
      return;
    }
    router.push(path, "forward", "push");
  };

  /**
   * The live badge for a settings row, or `undefined` for one that has none.
   *
   * ⛔ THE NUMBER IS THE KERNEL'S. The Messages row is not here any more: its unread-reply
   * indicator is the green "New" (`NewMessageIndicator`, F-482), driven by `sync.unread_replies`
   * in `renderSettingsItem`; opening the thread posts the read receipt and the next sync reads 0.
   * ⚠️ `undefined`, never `0` — `renderSettingsItem` renders any truthy badge, and a literal
   * zero on the row would say "nothing is waiting" in a red pill.
   */
  const badgeFor = (itemId: string): number | undefined => {
    if (itemId === "daily-check-in" && sync.surveys_outstanding > 0) {
      return sync.surveys_outstanding;
    }
    return undefined;
  };

  const renderSettingsItem = (item: SettingsItemType) => (
    <IonItem
      key={item.id}
      button
      detail={false}
      className="settings-item"
      onClick={() =>
        item.action
          ? item.action()
          : item.route && handleNavigation(item.route, item.id)
      }
    >
      <IonLabel>{item.label}</IonLabel>
      {/* F-486 + F-495: a waiting survey gets the SAME green "New" as an unread reply. */}
      {item.id === "care-team-surveys" && sync.surveys_outstanding > 0 && (
        <NewMessageIndicator count={sync.surveys_outstanding} what={SURVEYS_WAITING} />
      )}
      {/* F-482 (UAT3): an unread care-team reply is a green "New", never the red count pill —
          red reads as an alert. Other rows keep their badge. */}
      {item.id === "care-team-messages" && sync.unread_replies > 0 ? (
        <NewMessageIndicator count={sync.unread_replies} />
      ) : (
        (badgeFor(item.id) ?? item.badge) && (
          // F-538 (Leah, UAT — ruling 1): the Daily check-in row's number is GREEN (the app's
          // success token, the same lime as the "New" marker), never the red alert pill.
          <div
            className={`item-badge${item.id === "daily-check-in" ? " item-badge-success" : ""}`}
            data-testid={`settings-badge-${item.id}`}
          >
            {badgeFor(item.id) ?? item.badge}
          </div>
        )
      )}
      <IonIcon icon={chevronForward} slot="end" className="chevron-icon " />
      <IonRippleEffect />
    </IonItem>
  );

  return (
    <IonPage>
      <Header
        showGreeting={true}
        greetingText="My Settings"
        showPageTitle={false}
        showUserIcon={false}
        pageTitle="My Settings"
      />
      <IonContent className="settings-page-content">
        {sectionsLoading ? (
          <SettingsSkeleton
            sections={([...(isStaff ? [PROVIDER_PORTAL_SECTION] : []), ...SETTINGS_SECTIONS] as SettingsSectionType[])
              .filter((section) => !(isDesktop && section.mobileOnly))}
          />
        ) : ([...(isStaff ? [PROVIDER_PORTAL_SECTION] : []), ...SETTINGS_SECTIONS] as SettingsSectionType[])
          .filter((section) => !(isDesktop && section.mobileOnly))
          // ⛔ F-885 — an UNANSWERED features call is not "no organization". The hook has
          // already been through the shared 401 refresh-and-retry; if it still failed, CARE TEAM
          // stays on screen with an error and a retry instead of silently disappearing. Its rows
          // stay hidden (fail closed: the org may have switched them off).
          .filter((section) => section.id !== "care-team" || hasOrganization || featuresError)
          .map((section) => (
          <div className="section" key={section.id}>
            <div className="section-label">{section.label}</div>
            <IonList className="settings-list">
              <IonItemGroup>
                {section.id === "care-team" && featuresError ? (
                  <IonItem
                    button
                    detail={false}
                    className="settings-item"
                    data-testid="care-team-error"
                    onClick={() => void refreshFeatures()}
                  >
                    <IonLabel className="ion-text-wrap">
                      {CARE_TEAM_LOAD_ERROR}
                    </IonLabel>
                    <IonLabel slot="end" style={{ flex: "none", color: "#0778cf" }}>
                      {CARE_TEAM_RETRY}
                    </IonLabel>
                    <IonRippleEffect />
                  </IonItem>
                ) : section.items
                  .filter(
                    (item) =>
                      item.id !== "care-team-surveys" ||
                      features.surveys_enabled ||
                      sync.surveys_outstanding > 0,
                  )
                  // ⛔ F-514 (UAT, Leah) — NO MESSAGES ROW WHILE THE ORGANIZATION HAS MESSAGING
                  // OFF. `messaging_enabled` is the EFFECTIVE switch the kernel resolves for this
                  // patient (`GET /me/engagement/features`) — the same one it enforces on every
                  // write with `MESSAGING_DISABLED` — so it follows the org (and any location
                  // scope the kernel applies) without this app knowing how it is decided. F-490
                  // only removed the "messaging is turned off" popup; the entry itself goes too.
                  .filter((item) => item.id !== "care-team-messages" || features.messaging_enabled)
                  .map((item) => renderSettingsItem(item))}
              </IonItemGroup>
            </IonList>
          </div>
        ))}

        {lastFetchedWeeklyStatus && (
          <div
            className="text-white"
            style={{
              fontSize: "0.85em",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "2px 0",
              padding: "0",
            }}
          >
            Last refreshed:{" "}
            {new Date(lastFetchedWeeklyStatus).toLocaleString("en-CA", {
              year: "numeric",
              month: "short",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
              hour12: true,
            })}
          </div>
        )}
        <div
          className="text-white"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "4px 0",
          }}
        >
          App Version {APP_VERSION}{" "}
          {/* ⚠️ Name the ACTUAL environment. This used to print "- Staging" for every
              non-production build, so a dev build and a staging build were
              indistinguishable on screen — during the parallel run testers are holding
              both, and the one thing they need to know is which backend they are on. */}
          {CURRENT_ENV === "production" ? "" : `- ${CURRENT_ENV.charAt(0).toUpperCase()}${CURRENT_ENV.slice(1)}`}
        </div>
        <div
          className="text-white"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "4px 0",
          }}
        >
          Web Version {WEB_VERSION}{" "}
          {`${CURRENT_ENV.includes("production") ? "" : "- Staging"}`}
        </div>
        {Capacitor.getPlatform() !== "web" && (
          <div
            className="text-white"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              margin: "4px 0",
            }}
          >
            {Capacitor.getPlatform() === "ios"
              ? IOS_CATALOG_NUMBER
              : ANDROID_CATALOG_NUMBER}{" "}
          </div>
        )}

        {debugMode && (
          <div className="section" style={{ paddingBottom: "100px" }}>
            <div className="section-label">DEBUG</div>
            <IonList className="settings-list">
              <IonItemGroup>
                <IonItem detail={false} className="settings-item ">
                  <AllPreferencesButton />
                  <IonIcon
                    icon={chevronForward}
                    slot="end"
                    className="chevron-icon"
                  />
                  <IonRippleEffect />
                </IonItem>
                <IonItem detail={false} className="settings-item">
                  <LogViewer />
                  <IonIcon
                    icon={chevronForward}
                    slot="end"
                    className="chevron-icon"
                  />
                  <IonRippleEffect />
                </IonItem>
                <IonItem
                  button
                  detail={false}
                  className="settings-item"
                  onClick={() =>
                    router.push(ROUTES.HEALTH_PLUGIN_TESTER, "forward", "push")
                  }
                >
                  <IonLabel>Health Plugin Tester</IonLabel>
                  <IonIcon
                    icon={chevronForward}
                    slot="end"
                    className="chevron-icon"
                  />
                  <IonRippleEffect />
                </IonItem>
              </IonItemGroup>
            </IonList>
          </div>
        )}
        <div className="mb-4"></div>
        {/* Footer spacer for bottom nav pill */}
        <div style={{ height: "100px" }} />
      </IonContent>
    </IonPage>
  );
};

export default SettingsPage;
